import React, { useState, useEffect } from "react";
import { Paper, Tooltip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { customTheme } from "../customTheme";
import { vehicleSvgs } from "../sharedData";
import Skeleton from "@material-ui/lab/Skeleton";
import Chart from "react-apexcharts";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const useStyles = makeStyles({
	summaryBox: {
		padding: customTheme.spacing(3, 3),
	},
	twoColumnGrid: {
		padding: customTheme.spacing(1, 0, 2),
		display: "grid",
	},
	flexContainer: {
		display: "flex",
		alignItems: "center",
	},
	summaryVehicleIcon: {
		width: 18,
		height: 18,
	},
	summaryBoxLabel1: {
		fontSize: "0.74em",
		color: customTheme.palette.grey[500],
		margin: customTheme.spacing(0, 2, 0, 1),
	},
	paymentSummaryBoxLabel1: {
		fontSize: "0.74em",
		color: customTheme.palette.grey[500],
	},
	summaryBoxLabel2: {
		fontSize: 12,
		color: customTheme.palette.grey[500],
		marginRight: customTheme.spacing(2),
	},
	summaryBoxValue1: {
		fontSize: 13,
		color: customTheme.palette.grey[900],
	},
	summaryBoxValue2: {
		fontSize: 22,
		color: customTheme.palette.grey[900],
	},
	tooltip: {
		fontSize: 13,
	},
});

const ReactApexChart = ({ title, data }) => {
	const chartDataInit = {
		series: [
			{
				data: [31, 40, 28, 51, 42, 109, 100],
			},
		],
		options: {
			chart: {
				height: 50,
				width: "90%",
				parentHeightOffset: 0,
				type: "area",
				zoom: {
					enabled: false,
				},
				toolbar: {
					show: false,
				},
				sparkline: {
					enabled: true,
				},
				grid: {
					show: false,
				},
			},
			fill: {
				colors: "#18cc69",
			},
			dataLabels: {
				enabled: false,
			},
			stroke: {
				show: true,
				curve: "smooth",
				colors: "#18cc69",
			},
			xaxis: {
				show: false,
				labels: {
					show: false,
				},
				axisBorder: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
			},
			yaxis: {
				show: false,
				labels: {
					show: false,
				},
				axisBorder: {
					show: false,
				},
				axisTicks: {
					show: false,
				},
			},
			grid: {
				show: false,
			},
		},
	};

	const [chartData, setChartData] = useState(chartDataInit);

	useEffect(() => {
		setChartData((prev) => {
			if (
				data.vehicleSummaryByPeriod &&
				title !== "Unpaid Amount" &&
				title !== "Not Exited"
			) {
				let tempData = data.vehicleSummaryByPeriod.map((val) =>
					title === "Vehicle In"
						? { x: val.date, y: val.Entered }
						: title === "Vehicle Out"
						? { x: val.date, y: val.Exited }
						: title === "Paid Amount"
						? { x: val.date, y: val.Amount }
						: null
				);
				return { ...prev, series: [{ name: title, data: [...tempData] }] };
			} else if (
				data &&
				(title === "Issue" ||
					title === "Recharge" ||
					title === "Vehicle In" ||
					title === "Vehicle Out" ||
					title === "Enter" ||
					title === "Exit")
			) {
				if (title === "Issue") {
					return {
						...prev,
						series: [{ name: title, data: [...data.issueGraphData] }],
					};
				}
				if (title === "Recharge") {
					return {
						...prev,
						series: [{ name: title, data: [...data.rechargeGraphData] }],
					};
				}
				if (title === "Vehicle In") {
					return {
						...prev,
						series: [{ name: title, data: [...data.vehicleEntryGraphData] }],
					};
				}
				if (title === "Vehicle Out") {
					return {
						...prev,
						series: [{ name: title, data: [...data.vehicleExitGraphData] }],
					};
				}
				if (title === "Enter") {
					return {
						...prev,
						series: [{ name: title, data: [...data.vehicleEntryGraphData] }],
					};
				}
				if (title === "Exit") {
					return {
						...prev,
						series: [{ name: title, data: [...data.vehicleExitGraphData] }],
					};
				}
			} else {
				return {
					...prev,
					series: [{ name: title, data: [...data.graphData] }],
				};
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div style={{ position: "absolute", bottom: 0, width: "100%" }}>
			<Chart
				options={chartData.options}
				series={chartData.series}
				type="area"
				height={50}
				width={"100%"}
			/>
		</div>
	);
};

export default function ParkingSummaryCard({
	title,
	data,
	showGraph,
	getValue,
	valueDescription,
	valueDescriptionInfo,
	getTypes,
	getTypeValue,
	getTypeValueDescription,
	getAmount,
	label2,
	getValue2,
	subTypes,
	getSubTypeValue,
	unpaidAmount,
	showPaidUnpaidAmount,
	showUniqueParking,
	valueDescriptionTooltip,
	...props
}) {
	const classes = useStyles();

	const [paymentDistributions] = useState(
		sessionStorage.getItem("payment_distributions")
	);

	const renderTotalAmount = (data) => {
		if (paymentDistributions === "PM") {
			return {
				totalAmount: data.amount + data.monthlyPass_total_amount,
				label1: "POS",
				label2: "Monthly Pass",
				label1Amount: data.amount,
				label2Amount: data.monthlyPass_total_amount,
			};
		}
		if (paymentDistributions === "PP") {
			return {
				totalAmount: data.amount + data.monthlyPass_total_amount,
				label1: "POS",
				label2: "Permits",
				label1Amount: data.amount,
				label2Amount: data.permitPayments_total_amount,
			};
		}
		if (paymentDistributions === "MP") {
			return {
				totalAmount:
					data.permitPayments_total_amount + data.monthlyPass_total_amount,
				label2: "Permits",
				label1: "Monthly Pass",
				label2Amount: data.permitPayments_total_amount,
				label1Amount: data.monthlyPass_total_amount,
			};
		}
		if (paymentDistributions === "PMP") {
			return {
				totalAmount:
					data.amount +
					data.monthlyPass_total_amount +
					data.permitPayments_total_amount,
				label1: "POS",
				label2: "Monthly Pass",
				label3: "Permits",
				label1Amount: data.amount,
				label2Amount: data.monthlyPass_total_amount,
				label3Amount: data.permitPayments_total_amount,
			};
		}
	};
	return (
		<>
			{getValue ? (
				<Paper style={{ position: "relative", paddingBottom: "50px" }}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: getAmount ? "50% 50%" : "100%",
							marginBottom: getAmount ? 5 : 0,
							padding: "24px 24px 0px 24px",
						}}>
						<div>
							<Typography className={classes.summaryBoxLabel2}>
								{title}
							</Typography>
							{data ? (
								<div style={{ display: "flex" }}>
									<Typography className={classes.summaryBoxValue2}>
										{getValue(data)}
									</Typography>

									<div style={{ display: "flex", alignItems: "center" }}>
										{valueDescription && (
											<div style={{ fontSize: 14, margin: "5px" }}>
												{valueDescription}
											</div>
										)}
										{valueDescriptionTooltip && (
											<Tooltip
												arrow
												title={valueDescriptionTooltip}
												classes={{ tooltip: classes.tooltip }}>
												<InfoOutlinedIcon
													style={{ fontSize: 16, color: "#888" }}
												/>
											</Tooltip>
										)}
									</div>
								</div>
							) : (
								<Skeleton variant="text" height={36} width="34%" />
							)}
						</div>

						{getAmount && (
							<div>
								<Typography className={classes.summaryBoxLabel2}>
									Amount
								</Typography>
								{data ? (
									<Typography className={classes.summaryBoxValue2}>
										{getAmount(data)}
									</Typography>
								) : (
									<Skeleton variant="text" height={36} width="34%" />
								)}
							</div>
						)}
					</div>

					<div
						className={classes.twoColumnGrid}
						style={{
							gridRowGap: data ? 3 : 0,
							padding: "10px 24px 15px 24px",
						}}>
						{label2 && (
							<Typography
								className={classes.summaryBoxLabel1}
								style={{ flex: 1, textAlign: "right", marginRight: 0 }}>
								{label2}
							</Typography>
						)}
						{data
							? Object.keys(getTypes(data)).map((type) => (
									<>
										<div key={type} className={classes.flexContainer}>
											<img
												className={classes.summaryVehicleIcon}
												src={vehicleSvgs[type]}
												alt={getTypes(data)[type].label}
											/>
											<Typography
												className={classes.summaryBoxLabel1}
												style={{ marginRight: getValue2 ? 8 : 16 }}>
												{getTypes(data)[type].label}:
											</Typography>
											<Typography className={classes.summaryBoxValue1}>
												{getTypeValue(getTypes(data)[type])}
											</Typography>
											<div style={{ display: "flex", alignItems: "center" }}>
												{getTypeValueDescription && (
													<div style={{ fontSize: 12, margin: "0 5px" }}>
														{getTypeValueDescription(getTypes(data)[type])}
													</div>
												)}
												{/* {getTypeValueDescription && valueDescriptionInfo && <Tooltip arrow title={valueDescriptionInfo} classes={{ tooltip: classes.tooltip }}><InfoOutlinedIcon style={{ fontSize: 15, color: "#888" }} /></Tooltip>} */}
											</div>
											{(getAmount || getValue2) && (
												<Typography
													className={classes.summaryBoxValue1}
													style={{
														flex: 1,
														textAlign: "right",
														marginLeft: 5,
													}}>
													{getAmount
														? getAmount(getTypes(data)[type])
														: `₹ ${getValue2(getTypes(data)[type])}`}
												</Typography>
											)}
										</div>

										{subTypes ? (
											<div
												style={{
													display: "grid",
													gridAutoFlow: "column",
													justifyContent: "space-between",
													alignItems: "center",
													position: "relative",
													top: -4,
												}}>
												{Object.keys(subTypes).map((subType) => (
													<div
														style={{ display: "flex", alignItems: "center" }}>
														<Typography
															className="summaryBoxLabel3"
															style={{ marginRight: 8 }}>
															{subTypes[subType]}:
														</Typography>
														<Typography className="summaryBoxValue3">
															{getSubTypeValue(getTypes(data)[type][subType])}
														</Typography>
													</div>
												))}
											</div>
										) : null}
									</>
							  ))
							: [
									"75%",
									"60%",
									"80%",
									"50%",
									"40%",
									"50%",
									"45%",
									"65%",
									"50%",
							  ].map(
									(width, index) =>
										(!props.vehicleTypesCount ||
											index < props.vehicleTypesCount) && (
											<Skeleton
												variant="text"
												width={width}
												height={22}
												key={index}
											/>
										)
							  )}
					</div>
					{showGraph && data && <ReactApexChart title={title} data={data} />}
				</Paper>
			) : (
				<Paper style={{ position: "relative", paddingBottom: "50px" }}>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: getAmount ? "50% 50%" : "100%",
							marginBottom: getAmount ? 5 : 0,
							padding: "24px 24px 0px 24px",
						}}>
						<div>
							<Typography className={classes.summaryBoxLabel2}>
								{title}
							</Typography>
							{data ? (
								<Typography className={classes.summaryBoxValue2}>
									{renderTotalAmount(data)["totalAmount"]}
								</Typography>
							) : (
								<Skeleton variant="text" height={36} width="34%" />
							)}
						</div>
					</div>

					<div
						className={classes.twoColumnGrid}
						style={{
							gridRowGap: data ? 3 : 0,
							padding: "10px 24px 15px 24px",
						}}>
						{data ? (
							<>
								{(paymentDistributions === "PM" ||
									paymentDistributions === "PP" ||
									paymentDistributions === "MP") && (
									<>
										<div className={classes.flexContainer}>
											<Typography className={classes.paymentSummaryBoxLabel1}>
												{renderTotalAmount(data)["label1"]} :
											</Typography>
											<Typography
												className={classes.summaryBoxValue1}
												style={{
													flex: 1,
													textAlign: "right",
													marginLeft: 5,
												}}>{`₹ ${
												renderTotalAmount(data)["label1Amount"]
											}`}</Typography>
										</div>
										<div className={classes.flexContainer}>
											<Typography className={classes.paymentSummaryBoxLabel1}>
												{renderTotalAmount(data)["label2"]} :
											</Typography>
											<Typography
												className={classes.summaryBoxValue1}
												style={{
													flex: 1,
													textAlign: "right",
													marginLeft: 5,
												}}>{`₹ ${
												renderTotalAmount(data)["label2Amount"]
											}`}</Typography>
										</div>
									</>
								)}

								{paymentDistributions === "PMP" && (
									<>
										<div className={classes.flexContainer}>
											<Typography className={classes.paymentSummaryBoxLabel1}>
												{renderTotalAmount(data)["label1"]} :
											</Typography>
											<Typography
												className={classes.summaryBoxValue1}
												style={{
													flex: 1,
													textAlign: "right",
													marginLeft: 5,
												}}>{`₹ ${
												renderTotalAmount(data)["label1Amount"]
											}`}</Typography>
										</div>
										<div className={classes.flexContainer}>
											<Typography className={classes.paymentSummaryBoxLabel1}>
												{renderTotalAmount(data)["label2"]} :
											</Typography>
											<Typography
												className={classes.summaryBoxValue1}
												style={{
													flex: 1,
													textAlign: "right",
													marginLeft: 5,
												}}>{`₹ ${
												renderTotalAmount(data)["label2Amount"]
											}`}</Typography>
										</div>
										<div className={classes.flexContainer}>
											<Typography className={classes.paymentSummaryBoxLabel1}>
												{renderTotalAmount(data)["label3"]} :
											</Typography>
											<Typography
												className={classes.summaryBoxValue1}
												style={{
													flex: 1,
													textAlign: "right",
													marginLeft: 5,
												}}>{`₹ ${
												renderTotalAmount(data)["label3Amount"]
											}`}</Typography>
										</div>
									</>
								)}
							</>
						) : (
							[
								"75%",
								"60%",
								"80%",
								"50%",
								"40%",
								"50%",
								"45%",
								"65%",
								"50%",
							].map(
								(width, index) =>
									(!props.vehicleTypesCount ||
										index < props.vehicleTypesCount) && (
										<Skeleton
											variant="text"
											width={width}
											height={22}
											key={index}
										/>
									)
							)
						)}
					</div>
					{showGraph && data && <ReactApexChart title={title} data={data} />}
				</Paper>
			)}
		</>
	);
}
